import { Form, Formik } from "formik";
import React, { useContext } from "react";
import { useMessageSource } from "react-message-source";
import FlowHeader from "../../ui/widgets/flow-header";
import Info from "../../ui/widgets/info";
import CustomLink from "../../ui/widgets/link";
import Typography from "../../ui/widgets/typography";
import CustomTypography from "../../ui/widgets/typography";
import ChoosePerson from "../shared/components/choose-person";
import ProductImage from "../shared/components/product-image";
import Persons from "./persons";
import { AttorneyValues, PowerOfAttorneyModel } from "./types";
import usePowerOfAttorneyValidationSchema from "./validator";
import ApplicationPageContent from "../../ui/layout/page/content/application";
import ContentFooter from "../../ui/layout/page/content/footer";
import NavigationBar from "../../ui/layout/page/content/footer/navigation-bar";
import ContentHeader from "../../ui/layout/page/content/header";
import SectionFullWidthContainer from "../../ui/layout/page/forms-container/section-full-width-container";
import TypographyContainer from "../../ui/layout/page/typography-container/typography-base-container";
import TypographyItemContainer from "../../ui/layout/page/typography-container/typography-item-container";
import CustomList from "../../ui/widgets/list";
import CustomListItem from "../../ui/widgets/list/list-item";
import { mapAttorneyTypeToEvent } from "../../util/tracking/eventActionMapper";
import GTM from "../../util/tracking/gtm";
import { EventCategory } from "../../util/tracking/types";
import { LanguageContext } from "../../context/languageContext";
import "./styles.scss";
import InvalidFormError, {
  isSubmitErrorMessageShown,
} from "../../ui/layout/page/content/footer/form-error/invalid-form-error";
import { CancelApplicationContext } from "../../context/cancelApplicationContext";
import { ProductContext } from "../../context/product-context/productContext";
import { getPowerOfAttorneyTermsAndConditions } from "../../api/commercial/terms-and-conditions";
import { type CorrespondenceLanguage } from "../../api/commercial/generated-types";
import PowerOfAttorneyMapper from "./mapper";

export interface PowerOfAttorneyContextProps {
  selectedProduct: string;
  companyName: string;
  basicAccountApplication: string;
  pdfLanguage: CorrespondenceLanguage;
}

interface PowerOfAttorneyProps {
  initial: PowerOfAttorneyModel;
  context: PowerOfAttorneyContextProps;
  next: (model: PowerOfAttorneyModel) => void;
  previous: () => void;
}

export default function PowerOfAttorney({
  initial,
  context,
  next,
  previous,
}: PowerOfAttorneyProps): React.JSX.Element {
  const { getMessage } = useMessageSource();
  const { language } = useContext(LanguageContext);
  const { selectedProduct } = useContext(ProductContext);
  const { setCancelDialogActive } = useContext(CancelApplicationContext);
  return (
    <>
      <FlowHeader
        title={getMessage("FlowHeader.basicAccountApplication")}
        image={<ProductImage product={selectedProduct?.productRef} />}
        subTitle={context.companyName}
      />
      <ApplicationPageContent>
        <TypographyContainer>
          <TypographyItemContainer>
            <ContentHeader title={getMessage("Attorneys.title")} />
          </TypographyItemContainer>
        </TypographyContainer>
        <Formik
          key={language}
          enableReinitialize
          initialValues={initial}
          validationSchema={usePowerOfAttorneyValidationSchema()}
          onSubmit={async (values, formikActions) => {
            await next(values);
            formikActions.setSubmitting(false);
          }}
        >
          {({ values, handleSubmit, isSubmitting, errors, submitCount }) => {
            const { attorneysData, adminAttorneysData } = values;

            const hasFlexibleInvoicing =
              selectedProduct?.hasFeature("FLEXIBLE_INVOICING");

            const firstAttorneyBillingInfo = hasFlexibleInvoicing ? (
              ""
            ) : (
              <SectionFullWidthContainer>
                <Info>{getMessage("Attorneys.billingInfo")}</Info>
              </SectionFullWidthContainer>
            );

            let attorneysDataSection = null;

            if (values.attorneyType === AttorneyValues.OTHER) {
              attorneysDataSection = (
                <React.Fragment>
                  {firstAttorneyBillingInfo}
                  <Persons name="attorneysData" formData={attorneysData} />
                </React.Fragment>
              );
            } else if (
              values.attorneyType === AttorneyValues.ADMIN ||
              values.attorneyType === AttorneyValues.NOT_CHOSEN
            ) {
              attorneysDataSection = (
                <>
                  {firstAttorneyBillingInfo}
                  <Persons
                    name="adminAttorneysData"
                    formData={adminAttorneysData}
                  />
                </>
              );
            } else if (values.attorneyType === AttorneyValues.NONE) {
              attorneysDataSection = (
                <CustomTypography variant="body1">
                  {getMessage("Attorneys.NONE-warning-1")}
                  <CustomLink
                    noicon
                    href={getMessage("Attorneys.formOfProxy-url")}
                  >
                    {getMessage("Attorneys.formOfProxy")}
                  </CustomLink>
                  {getMessage("Attorneys.NONE-warning-2")}
                </CustomTypography>
              );
            }

            return (
              <Form>
                <SectionFullWidthContainer>
                  <Info title={getMessage("Attorneys.info-label")}>
                    <div className="scd-section-pusher-md">
                      <TypographyItemContainer>
                        <Typography variant="body2">
                          {getMessage("Attorneys.info")}
                        </Typography>
                      </TypographyItemContainer>
                      <CustomList isBulletsList={true}>
                        <CustomListItem>
                          {getMessage("Attorneys.info-1")}
                        </CustomListItem>
                        <CustomListItem>
                          {getMessage("Attorneys.info-2")}
                        </CustomListItem>
                        <CustomLink
                          href={getPowerOfAttorneyTermsAndConditions(
                            PowerOfAttorneyMapper.mapFrontendLanguageToCorrespondenceLanguage(
                              language,
                            ),
                          )}
                          target="_blank"
                        >
                          {getMessage("Attorneys.termsAndConditions")}
                        </CustomLink>
                      </CustomList>
                    </div>
                  </Info>
                  <div className="scd-section-pusher-lg">
                    <SectionFullWidthContainer>
                      <ChoosePerson
                        title={getMessage("Attorneys.select")}
                        id="attorneyType"
                        name="attorneyType"
                        values={[
                          {
                            id: "attorneyType-admin",
                            value: AttorneyValues.ADMIN,
                            messageKey: "Attorneys.ADMIN",
                            person: adminAttorneysData[0],
                          },
                          {
                            id: "attorneyType-other",
                            value: AttorneyValues.OTHER,
                            messageKey: "Attorneys.OTHER",
                          },
                          {
                            id: "attorneyType-none",
                            value: AttorneyValues.NONE,
                            messageKey: "Attorneys.NONE",
                          },
                        ]}
                        onChange={(_, newValue) => {
                          GTM.trackEvent(
                            EventCategory.AuthorizedRepresentative,
                            mapAttorneyTypeToEvent(newValue),
                          );
                        }}
                      />
                    </SectionFullWidthContainer>
                  </div>
                  <div className="scd-section-pusher-lg">
                    <SectionFullWidthContainer>
                      {attorneysDataSection}
                    </SectionFullWidthContainer>
                  </div>
                </SectionFullWidthContainer>
                <ContentFooter>
                  <InvalidFormError
                    isDisplayed={isSubmitErrorMessageShown(submitCount, errors)}
                  />
                  <NavigationBar
                    onCancel={() => setCancelDialogActive(true)}
                    cancelButtonText={getMessage("Onboarding.cancel")}
                    isSubmitting={isSubmitting}
                    backButtonText={getMessage("Onboarding.back")}
                    nextButtonText={getMessage("Onboarding.continue")}
                    onBack={previous}
                    onNext={handleSubmit}
                  />
                </ContentFooter>
              </Form>
            );
          }}
        </Formik>
      </ApplicationPageContent>
    </>
  );
}
